"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import { useAuth } from "@/contexts/AuthContext";

interface AppLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  breadcrumbs?: { label: string; href?: string }[];
  userRole?: "administrator" | "internal_user" | "client_user" | "consultant";
  headerActions?: React.ReactNode;
}

export default function AppLayout({
  children,
  userRole = "internal_user",
  pageTitle,
  breadcrumbs = [],
  headerActions,
}: AppLayoutProps) {
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedSidebar = localStorage.getItem("ipdocket_sidebar_collapsed");
      if (storedSidebar === "true") {
        setCollapsed(true);
      }
    }
  }, []);
  const redirectedRef = useRef(false);
  const pathname = usePathname();

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (loading) return;
    if (!user && !redirectedRef.current) {
      redirectedRef.current = true;
      window.location.replace("/sign-up-login-screen");
    }
  }, [loading, user]);

  useEffect(() => {
    if (!loading) return;

    const timeoutId = window.setTimeout(() => {
      if (!user && !redirectedRef.current) {
        redirectedRef.current = true;
        window.location.replace("/sign-up-login-screen");
      }
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [loading, user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="relative flex items-center justify-center w-12 h-12">
          <div className="absolute inset-0 rounded-full border-[3px] border-primary/20"></div>
          <div className="absolute inset-0 rounded-full border-[3px] border-primary border-t-transparent animate-spin"></div>
        </div>
        <p className="text-sm font-medium text-muted-foreground animate-pulse tracking-wide">
          Loading IPDocket...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        userRole={
          user?.roles.includes("admin")
            ? "administrator"
            : user?.roles.includes("agent")
              ? "internal_user"
              : user?.roles.includes("consultant")
                ? "consultant"
                : user?.roles.includes("client")
                  ? "client_user"
                  : userRole
        }
      />

      <Topbar
        sidebarCollapsed={collapsed}
        onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
        pageTitle={pageTitle}
        breadcrumbs={breadcrumbs}
      />

      {/* Main content */}
      <main
        className={`
          min-h-screen pt-16 transition-all duration-300 ease-in-out overflow-x-hidden
          pl-0 ${collapsed ? "md:pl-16" : "md:pl-60"}
        `}
      >
        <div className="max-w-screen-2xl mx-auto px-6 py-6 xl:px-8 2xl:px-10">
          {children}
        </div>
      </main>
    </div>
  );
}
